/**
 * Centralized Labels Configuration for GenericFormsPage
 * 
 * This file contains all text labels, messages, and UI strings used in the GenericFormsPage component.
 * Modify these values to customize the text throughout your application.
 * 
 * Usage:
 * import { getLabels, LABEL_CATEGORIES } from './labels';
 * const labels = getLabels(entityName, customLabels);
 */

// Default label templates - use {entityName} for dynamic entity names
export const DEFAULT_LABELS = {
  // Search & Filters
  searchPlaceholder: 'Search {entityName}s...',
  showLabel: 'Show:',
  hideFiltersTooltip: 'Hide filters',
  showFiltersTooltip: 'Show filters',
  quickFiltersTooltip: 'Quick apply filter presets',
  allFiltersText: 'All Filters',
  clearAllText: 'Clear All',
  filterPresetsText: 'Filter Presets',
  minPlaceholder: 'Min',
  maxPlaceholder: 'Max',
  filtersLabel: 'Filters',
  
  // Actions & Tooltips
  viewTooltip: 'View',
  editTooltip: 'Edit',
  duplicateTooltip: 'Duplicate {entityName}',
  duplicateTitle: 'Duplicate {entityName}',
  duplicateText: 'Duplicate',
  duplicateConfirmText: 'Are you sure you want to duplicate this {entityName}? A new record will be created with all data (except files).',
  deleteTooltip: 'Delete',
  addTooltip: 'Add {entityName}',
  exportTooltip: 'Export data',
  tablePresetsTooltip: 'Table presets and customization',
  previousPageTooltip: 'Previous page',
  nextPageTooltip: 'Next page',
  
  // Form Buttons
  createText: 'Create',
  updateText: 'Update',
  cancelText: 'Cancel',
  closeText: 'Close',
  deleteText: 'Delete',
  saveText: 'Save',
  
  // Form Titles
  addTitle: 'Add New {entityName}',
  editTitle: 'Edit {entityName}',
  viewTitle: 'View {entityName}',
  deleteTitle: 'Delete {entityName}',
  
  // Pagination
  showingText: 'Showing',
  ofText: 'of',
  filterText: 'filter',
  filtersText: 'filters',
  activeText: 'active',
  
  // Export Menu
  exportCsv: 'Export as CSV',
  exportJson: 'Export as JSON', 
  exportPdf: 'Export to PDF',
  exportExcel: 'Export to Excel',
  exportHtml: 'Export to HTML',
  
  // Field Placeholders & Prefixes
  selectPrefix: 'Select',
  enterPrefix: 'Enter',
  allPrefix: 'All',
  choosePrefix: 'Choose',
  
  // Status & State Messages
  loadingText: 'Loading...',
  noDataText: 'No {entityName}s found',
  emptyStateText: 'No data available',
  errorText: 'An error occurred',
  
  // Delete Modal
  deleteConfirmText: 'Are you sure you want to delete this {entityName}? This action cannot be undone.',
  deleteWarningText: 'This action cannot be undone.',
  
  // Filter Messages
  filterActiveText: '{count} {filterText} active',
  noFiltersText: 'No filters applied',
  
  // Table Messages
  noResultsText: 'No results found',
  sortByText: 'Sort by',
  columnsText: 'Columns',
  densityText: 'Density',
  
  // Form Validation
  requiredFieldText: 'This field is required',
  invalidFormatText: 'Invalid format',
  
  // Success Messages
  createSuccessText: '{entityName} created successfully!',
  updateSuccessText: '{entityName} updated successfully!',
  deleteSuccessText: '{entityName} deleted successfully!',
  
  // Error Messages
  createErrorText: 'Failed to create {entityName}',
  updateErrorText: 'Failed to update {entityName}',
  deleteErrorText: 'Failed to delete {entityName}',
  loadErrorText: 'Failed to load {entityName}s',
  
  // Preset Messages
  presetSavedText: 'Preset saved successfully',
  presetDeletedText: 'Preset deleted successfully',
  presetAppliedText: 'Preset applied successfully',
  
  // Common Actions
  refreshText: 'Refresh',
  resetText: 'Reset',
  clearText: 'Clear',
  applyText: 'Apply',
  backText: 'Back',
  nextText: 'Next',
  previousText: 'Previous',
  finishText: 'Finish',
  
  // Time & Date
  todayText: 'Today',
  yesterdayText: 'Yesterday',
  thisWeekText: 'This week',
  thisMonthText: 'This month',
  customRangeText: 'Custom range',
  
  // File & Export
  downloadText: 'Download',
  printText: 'Print',
  shareText: 'Share',
  copyText: 'Copy',
  
  // Accessibility
  menuText: 'Menu',
  optionsText: 'Options',
  settingsText: 'Settings',
  helpText: 'Help',
  
  // Tracking Section (moved from individual configs)
  trackingSectionTitle: '{entityName} Tracking Information',
  trackingSectionIcon: '📋',
  trackingFooterNote: 'This tracking information is automatically managed by the system and cannot be manually edited.',
  trackingFooterIcon: 'ℹ️',
  createdLabel: '{entityName} Created',
  createdIcon: '✨',
  updatedLabel: 'Last Updated',
  updatedIcon: '🔄',
  createdByLabel: 'By:',
  createdDateLabel: 'Date:',
  updatedByLabel: 'By:',
  updatedDateLabel: 'Date:',
  notSpecifiedText: 'Not specified',
  notModifiedText: 'Not modified yet',
  neverUpdatedText: 'Never updated',
  unknownUserText: 'Unknown User'
};

// Label categories for easier organization and customization
export const LABEL_CATEGORIES = {
  SEARCH_FILTERS: [
    'searchPlaceholder', 'showLabel', 'hideFiltersTooltip', 'showFiltersTooltip',
    'quickFiltersTooltip', 'allFiltersText', 'clearAllText', 'filterPresetsText',
    'minPlaceholder', 'maxPlaceholder', 'filtersLabel'
  ],
  
  ACTIONS: [
    'viewTooltip', 'editTooltip', 'duplicateTooltip', 'deleteTooltip', 'addTooltip', 'exportTooltip',
    'tablePresetsTooltip', 'previousPageTooltip', 'nextPageTooltip'
  ],
  
  FORM_BUTTONS: [
    'createText', 'updateText', 'cancelText', 'closeText', 'deleteText', 'saveText'
  ],
  
  FORM_TITLES: [
    'addTitle', 'editTitle', 'viewTitle', 'deleteTitle'
  ],
  
  PAGINATION: [
    'showingText', 'ofText', 'filterText', 'filtersText', 'activeText'
  ],
  
  EXPORT: [
    'exportCsv', 'exportJson', 'exportPdf', 'exportExcel', 'exportHtml'
  ],
  
  FIELD_PLACEHOLDERS: [
    'selectPrefix', 'enterPrefix', 'allPrefix', 'choosePrefix'
  ],
  
  MESSAGES: [
    'loadingText', 'noDataText', 'emptyStateText', 'errorText',
    'createSuccessText', 'updateSuccessText', 'deleteSuccessText',
    'createErrorText', 'updateErrorText', 'deleteErrorText', 'loadErrorText'
  ],
  
  TRACKING: [
    'trackingSectionTitle', 'trackingSectionIcon', 'trackingFooterNote',
    'trackingFooterIcon', 'createdLabel', 'createdIcon', 'updatedLabel',
    'updatedIcon', 'createdByLabel', 'createdDateLabel', 'updatedByLabel',
    'updatedDateLabel', 'notSpecifiedText', 'notModifiedText',
    'neverUpdatedText', 'unknownUserText'
  ]
};

/**
 * Get processed labels with entity name substitution and custom overrides
 * @param {string} entityName - The entity name (e.g., 'Document', 'User', 'Order')
 * @param {Object} customLabels - Custom label overrides
 * @returns {Object} Processed labels object
 */
export const getLabels = (entityName = 'Record', customLabels = {}) => {
  const processedLabels = {};
  
  // Process each default label
  Object.entries(DEFAULT_LABELS).forEach(([key, value]) => {
    // Replace {entityName} placeholders with actual entity name
    let processedValue = value.replace(/{entityName}/g, entityName);
    
    // Apply custom overrides
    if (customLabels[key]) {
      processedValue = customLabels[key].replace(/{entityName}/g, entityName);
    }
    
    processedLabels[key] = processedValue;
  });
  
  // Add any additional custom labels not in defaults
  Object.entries(customLabels).forEach(([key, value]) => {
    if (!DEFAULT_LABELS[key]) {
      processedLabels[key] = value.replace(/{entityName}/g, entityName);
    }
  });
  
  return processedLabels;
};

/**
 * Get labels for a specific category
 * @param {string} category - Category key from LABEL_CATEGORIES
 * @param {string} entityName - The entity name
 * @param {Object} customLabels - Custom label overrides
 * @returns {Object} Labels for the specified category
 */
export const getCategoryLabels = (category, entityName = 'Record', customLabels = {}) => {
  const allLabels = getLabels(entityName, customLabels);
  const categoryKeys = LABEL_CATEGORIES[category] || [];
  
  const categoryLabels = {};
  categoryKeys.forEach(key => {
    if (allLabels[key]) {
      categoryLabels[key] = allLabels[key];
    }
  });
  
  return categoryLabels;
};

/**
 * Create tracking section configuration with processed labels
 * @param {string} entityName - The entity name
 * @param {Object} customLabels - Custom label overrides
 * @param {Object} customConfig - Custom tracking configuration
 * @returns {Object} Complete tracking section configuration
 */
export const createTrackingConfig = (entityName = 'Record', customLabels = {}, customConfig = {}) => {
  const labels = getLabels(entityName, customLabels);
  
  return {
    enabled: true,
    title: labels.trackingSectionTitle,
    icon: labels.trackingSectionIcon,
    layout: 'double', // 'single' or 'double' columns
    showFooterNote: true,
    footerIcon: labels.trackingFooterIcon,
    footerNote: labels.trackingFooterNote,
    dateFormat: {
      locale: 'en-GB',
      options: {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }
    },
    fields: [
      {
        id: 'created',
        label: labels.createdLabel,
        icon: labels.createdIcon,
        color: 'text-green-600 dark:text-green-400',
        dataField: 'createdAt',
        userField: 'createdBy',
        showUser: true,
        showDate: true,
        userLabel: labels.createdByLabel,
        dateLabel: labels.createdDateLabel,
        userEmptyText: labels.notSpecifiedText,
        dateEmptyText: labels.notSpecifiedText
      },
      {
        id: 'updated',
        label: labels.updatedLabel,
        icon: labels.updatedIcon,
        color: 'text-blue-600 dark:text-blue-400',
        dataField: 'updatedAt',
        userField: 'updatedBy',
        showUser: true,
        showDate: true,
        userLabel: labels.updatedByLabel,
        dateLabel: labels.updatedDateLabel,
        userEmptyText: labels.notModifiedText,
        dateEmptyText: labels.neverUpdatedText
      }
    ],
    ...customConfig
  };
};

/**
 * Predefined label sets for common entity types
 */
export const ENTITY_LABEL_SETS = {
  document: {
    searchPlaceholder: 'Search documents...',
    addTooltip: 'Add Document',
    trackingSectionTitle: 'Document Tracking Information'
  },
  
  user: {
    searchPlaceholder: 'Search users...',
    addTooltip: 'Add User',
    trackingSectionTitle: 'User Account Information',
    createdLabel: 'Account Created',
    updatedLabel: 'Last Modified'
  },
  
  order: {
    searchPlaceholder: 'Search orders...',
    addTooltip: 'Create Order',
    trackingSectionTitle: 'Order Timeline',
    createdLabel: 'Order Placed',
    updatedLabel: 'Last Modified'
  },
  
  product: {
    searchPlaceholder: 'Search products...',
    addTooltip: 'Add Product',
    trackingSectionTitle: 'Product Information',
    createdLabel: 'Product Created',
    updatedLabel: 'Last Updated'
  }
};

/**
 * Get predefined labels for common entity types
 * @param {string} entityType - Entity type key (document, user, order, product)
 * @param {string} entityName - The entity name (optional, defaults to entityType)
 * @param {Object} customLabels - Additional custom overrides
 * @returns {Object} Processed labels
 */
export const getEntityLabels = (entityType, entityName = null, customLabels = {}) => {
  // Ensure entityType is a string
  const entityTypeStr = typeof entityType === 'string' ? entityType : String(entityType);
  const finalEntityName = entityName || entityTypeStr.charAt(0).toUpperCase() + entityTypeStr.slice(1);
  const entityLabels = ENTITY_LABEL_SETS[entityTypeStr] || {};
  
  return getLabels(finalEntityName, { ...entityLabels, ...customLabels });
};

export default {
  DEFAULT_LABELS,
  LABEL_CATEGORIES,
  ENTITY_LABEL_SETS,
  getLabels,
  getCategoryLabels,
  createTrackingConfig,
  getEntityLabels
};
